import React, { useState, useEffect } from 'react';
import { processOperationFlowData } from '../utils/operationFlowProcessing';
import operationFlowsData from '../data/operation_flows.json';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
  Tabs,
  Link,
  StatusIndicator,
  Cards,
  Modal,
  ExpandableSection,
  ButtonDropdown,
  TagEditor,
  Select,
  Alert
} from '@cloudscape-design/components';

export function OperationFlowsTab ({ project }) {
  const [data, setData] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [selectedApiGroup, setSelectedApiGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('workflows');

  useEffect(() => {
    const processed = processOperationFlowData(operationFlowsData);
    setData(processed);
  }, []);

  if (!data) return null;

  return (
    <SpaceBetween size="l">
      {/* Header Actions */}
      <Container
        header={
          <Header
            variant="h2"
            description="Analysis of business workflows, transaction patterns, and API groupings"
            actions={
              <ButtonDropdown
                items={[
                  { text: 'Export flows', id: 'export' },
                  { text: 'View as diagram', id: 'diagram' },
                  { text: 'Refresh analysis', id: 'refresh' }
                ]}
              >
                Actions
              </ButtonDropdown>
            }
          >
            Operation Flows Analysis
          </Header>
        }
      >
        <Tabs
          tabs={[
            {
              label: "Business Workflows",
              id: "workflows",
              content: (
                <SpaceBetween size="l">
                  <Cards
                    cardDefinition={{
                      header: item => (
                        <Header
                          variant="h3"
                          actions={
                            <Link onFollow={() => setSelectedWorkflow(item)}>
                              View details
                            </Link>
                          }
                          description={`Implemented in: ${item.source_file}`}
                        >
                          {item.name}
                        </Header>
                      ),
                      sections: [
                        {
                          id: 'workflow',
                          content: item => (
                            <SpaceBetween size="m">
                              <Box>
                                <Box variant="awsui-key-label">Workflow Steps</Box>
                                <Table
                                  columnDefinitions={[
                                    {
                                      id: 'operation',
                                      header: 'Operation',
                                      cell: item => item.operation
                                    },
                                    {
                                      id: 'service',
                                      header: 'Service',
                                      cell: item => (
                                        <StatusIndicator type="info">
                                          {item.service}
                                        </StatusIndicator>
                                      )
                                    }
                                  ]}
                                  items={item.steps}
                                  variant="embedded"
                                  stripedRows
                                  trackBy="operation"
                                />
                              </Box>
                              <ColumnLayout columns={2}>
                                <SpaceBetween size="xs">
                                  <Box variant="awsui-key-label">Services Involved</Box>
                                  <Box>
                                    {[...new Set(item.steps.map(step => step.service))].map((service, index) => (
                                      <StatusIndicator key={service} type="info">
                                        {service}
                                      </StatusIndicator>
                                    ))}
                                  </Box>
                                </SpaceBetween>
                                <SpaceBetween size="xs">
                                  <Box variant="awsui-key-label">Entities</Box>
                                  <Box>
                                    {[...new Set(item.steps.flatMap(step => step.entities_involved))].map((entity, index) => (
                                      <StatusIndicator key={entity} type="success">
                                        {entity}
                                      </StatusIndicator>
                                    ))}
                                  </Box>
                                </SpaceBetween>
                              </ColumnLayout>
                            </SpaceBetween>
                          )
                        }
                      ]
                    }}
                    items={data.workflows}
                    cardsPerRow={[
                      { cards: 1 },
                      { minWidth: 800, cards: 2 }
                    ]}
                    variant="full-page"
                    stickyHeader
                    empty={
                      <Box textAlign="center" color="inherit">
                        <b>No workflows</b>
                        <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                          No business workflows found in the analysis.
                        </Box>
                      </Box>
                    }
                  />

                  {/* Update Workflow Details Modal */}
                  <Modal
                    visible={!!selectedWorkflow}
                    onDismiss={() => setSelectedWorkflow(null)}
                    header={`Workflow: ${selectedWorkflow?.name}`}
                    size="large"
                  >
                    <SpaceBetween size="l">
                      <Container
                        header={<Header variant="h3">Implementation Details</Header>}
                      >
                        <ColumnLayout columns={2}>
                          <SpaceBetween size="s">
                            <Box variant="awsui-key-label">Sources</Box>
                            <Box>{selectedWorkflow?.implementations.join(', ')}</Box>
                          </SpaceBetween>
                        </ColumnLayout>
                      </Container>

                      <Container
                        header={<Header variant="h3">Workflow Steps</Header>}
                      >
                        <Table
                          columnDefinitions={[
                            {
                              id: 'operation',
                              header: 'Operation',
                              cell: item => item.operation
                            },
                            {
                              id: 'service',
                              header: 'Service',
                              cell: item => item.service
                            },
                            {
                              id: 'entities',
                              header: 'Entities',
                              cell: item => item.entities_involved.join(', ')
                            }
                          ]}
                          items={selectedWorkflow?.steps || []}
                          variant="embedded"
                        />
                      </Container>
                    </SpaceBetween>
                  </Modal>
                </SpaceBetween>
              )
            },
            {
              label: "Transaction Patterns",
              id: "patterns",
              content: (
                <Table
                  columnDefinitions={[
                    {
                      id: 'pattern_name',
                      header: 'Pattern',
                      cell: item => (
                        <Link onFollow={() => setSelectedPattern(item)}>
                          {item.pattern_name}
                        </Link>
                      )
                    },
                    {
                      id: 'operations',
                      header: 'Operations',
                      cell: item => item.operations_sequence.join(', ')
                    },
                    {
                      id: 'consistency',
                      header: 'Consistency Scope',
                      cell: item => (
                        <StatusIndicator type={
                          item.consistency_scope.toLowerCase().includes('write') ? 'warning' : 'info'
                        }>
                          {item.consistency_scope}
                        </StatusIndicator>
                      )
                    },
                    {
                      id: 'sources',
                      header: 'Sources',
                      cell: item => item.sources.join(', ')
                    }
                  ]}
                  items={data.patterns}
                  variant="container"
                  stickyHeader
                  stripedRows
                />
              )
            },
            {
              label: "API Groupings",
              id: "api",
              content: (
                <SpaceBetween size="l">
                  {data.apiGroups.map(group => (
                    <Container
                      key={group.group_name}
                      header={
                        <Header
                          variant="h3"
                          actions={
                            <Link onFollow={() => setSelectedApiGroup(group)}>
                              View details
                            </Link>
                          }
                        >
                          {group.group_name}
                        </Header>
                      }
                    >
                      <SpaceBetween size="m">
                        <ColumnLayout columns={2}>
                          <Box variant="awsui-key-label">
                            Business Capability: {group.business_capability}
                          </Box>
                          <Box variant="awsui-key-label">
                            Sources: {group.sources.join(', ')}
                          </Box>
                        </ColumnLayout>
                        <Table
                          columnDefinitions={[
                            {
                              id: 'method',
                              header: 'Method',
                              cell: item => (
                                <StatusIndicator type={
                                  item.method === 'GET' ? 'success' :
                                  item.method === 'POST' ? 'info' : 'warning'
                                }>
                                  {item.method}
                                </StatusIndicator>
                              )
                            },
                            {
                              id: 'path',
                              header: 'Path',
                              cell: item => item.path || '-'
                            },
                            {
                              id: 'operation',
                              header: 'Operation',
                              cell: item => item.business_operation
                            },
                            {
                              id: 'related',
                              header: 'Related Endpoints',
                              cell: item => item.related_endpoints.join(', ') || '-'
                            }
                          ]}
                          items={group.endpoints}
                          variant="embedded"
                        />
                      </SpaceBetween>
                    </Container>
                  ))}
                </SpaceBetween>
              )
            }
          ]}
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        />
      </Container>
    </SpaceBetween>
  );
};
